import {
	Button,
	Grid,
	InputLabel,
	MenuItem,
	Stack,
	TextField,
	useTheme,
} from '@mui/material';
import { IconSearch, IconX } from '@tabler/icons';
import MainCard from 'components/cards/MainCard';
import SkeltonTotalGrowthBarChart from 'components/cards/Skeleton/TotalGrowthBarChart';
import EmptyData from 'components/EmptyData/EmptyData';
import PageTitle from 'components/Title/PageTitle';
import { useFormik } from 'formik';
import getEnumOptionLabel from 'modules/shared/utils/getEnumOptionLabel';
import {
	formateDateToStringDate,
	generateDatesRangeArray,
	handleDateInputChange,
} from 'modules/utilities/Helpers';
import moment from 'moment';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import {
	EndUsersCountChartType,
	endUsersCountChartTypeEnumOptions,
} from '../constants/enums';
import { IChartProps } from '../interfaces/Charts';
import {
	IEndUsersCountChartForm,
	IEndUsersCountStatsByDate,
} from '../interfaces/Stats';
import {
	DashboardStateSelector,
	getActiveUsersCounts,
	getNewUsersCounts,
} from '../store/slice';

const EndUsersCountsGraph = () => {
	const theme: any = useTheme();
	const primaryMain = theme.palette.primary.main;

	const dispatch = useDispatch();
	const { activeNewUsersCounts, isLoading } = useSelector(
		DashboardStateSelector,
	);

	const handleUpdateChartData = (values: IEndUsersCountChartForm) => {
		const payload = {
			fromDate: moment(values.fromDate).format('YYYY-MM-DD'),
			toDate: moment(values.toDate).format('YYYY-MM-DD'),
		};
		if (values.type === EndUsersCountChartType.NEW) {
			dispatch(getNewUsersCounts(payload));
		} else {
			dispatch(getActiveUsersCounts(payload));
		}
	};

	const form = useFormik<IEndUsersCountChartForm>({
		validateOnChange: false,
		initialValues: getInitialValues(),
		validationSchema,
		onSubmit: values => {
			handleUpdateChartData(values);
			setShowClearButton(true);
		},
	});

	const [chartProps, setChartProps] = useState<IChartProps>();
	const [showClearButton, setShowClearButton] = useState<boolean>(false);

	const updateChartProps = () => {
		setChartProps(
			getChartProps(
				activeNewUsersCounts.data,
				form.values.fromDate,
				form.values.toDate,
				primaryMain,
			),
		);
	};

	const clearSearchInputs = (withUpdate: boolean = true) => {
		setShowClearButton(false);
		form.setValues(getInitialValues());
		if (withUpdate) handleUpdateChartData(getInitialValues());
	};

	useEffect(
		() => {
			if (!activeNewUsersCounts.isLoading) {
				updateChartProps();
			}
		},
		// eslint-disable-next-line
		[activeNewUsersCounts],
	);

	useEffect(
		() => {
			if (isLoading) clearSearchInputs(false);
		},
		// eslint-disable-next-line
		[isLoading],
	);

	const showEmptyStatus =
		!activeNewUsersCounts.isLoading &&
		activeNewUsersCounts.data.stats.length === 0;

	const chartRender =
		activeNewUsersCounts.isLoading || !chartProps ? (
			<SkeltonTotalGrowthBarChart />
		) : (
			<Chart {...chartProps} />
		);

	return (
		<MainCard
			noPageTitle
			title={
				<PageTitle>
					Number of{' '}
					{getEnumOptionLabel(
						endUsersCountChartTypeEnumOptions,
						form.values.type,
					)?.toLocaleLowerCase()}
				</PageTitle>
			}
		>
			<form onSubmit={form.handleSubmit}>
				<Stack justifyContent={'center'} alignItems='center'>
					<Grid
						container
						width='1000px'
						maxWidth='100%'
						alignItems='start'
						paddingBottom={2}
					>
						<Grid item md={4} xs={12} padding={2}>
							<Stack spacing={1}>
								<InputLabel htmlFor='type'>Users status</InputLabel>
								<TextField
									fullWidth
									name={'type'}
									id={'type'}
									placeholder='Type'
									size='small'
									select
									value={form.values.type}
									onChange={e => {
										const value = Number.parseInt(e.target.value);
										form.setFieldValue('type', value);
									}}
								>
									{endUsersCountChartTypeEnumOptions.map(type => (
										<MenuItem key={type.value} value={type.value}>
											{type.label}
										</MenuItem>
									))}
								</TextField>
							</Stack>
						</Grid>
						<Grid item md={4} xs={12} padding={2}>
							<Stack spacing={1}>
								<InputLabel htmlFor='fromDate'>From</InputLabel>
								<TextField
									fullWidth
									name={'fromDate'}
									size='small'
									id='fromDate'
									placeholder={`From date`}
									type={'date'}
									value={formateDateToStringDate(form.values.fromDate, true)}
									onChange={e =>
										handleDateInputChange(e, value =>
											form.setFieldValue('fromDate', value),
										)
									}
									error={!!form.errors.fromDate}
									helperText={form.errors.fromDate}
								/>
							</Stack>
						</Grid>
						<Grid item md={4} xs={12} padding={2}>
							<Stack spacing={1}>
								<InputLabel htmlFor='toDate'>To</InputLabel>
								<TextField
									fullWidth
									name={'toDate'}
									id='toDate'
									placeholder={`To date`}
									size='small'
									type={'date'}
									value={formateDateToStringDate(form.values.toDate, true)}
									onChange={e =>
										handleDateInputChange(e, value =>
											form.setFieldValue('toDate', value),
										)
									}
									error={!!form.errors.toDate}
									helperText={form.errors.toDate}
								/>
							</Stack>
						</Grid>

						<Grid
							item
							md={showClearButton ? 10 : 12}
							xs={12}
							padding={2}
							flexDirection={'row'}
						>
							<Button
								fullWidth
								variant='outlined'
								startIcon={<IconSearch />}
								size='large'
								type='submit'
							>
								Apply
							</Button>
						</Grid>
						{showClearButton && (
							<Grid item md={2} xs={12} padding={2}>
								<Button
									fullWidth
									sx={{ marginRight: 2 }}
									variant='outlined'
									color='error'
									size='large'
									startIcon={<IconX />}
									onClick={() => clearSearchInputs()}
								>
									Reset
								</Button>
							</Grid>
						)}
					</Grid>
				</Stack>
			</form>
			{showEmptyStatus ? (
				<EmptyData message='No data found try to reset the filters.' />
			) : (
				chartRender
			)}
		</MainCard>
	);
};

export default EndUsersCountsGraph;

const axisTitle = {
	d: 'Day',
	w: 'Week',
	m: 'Month',
	y: 'Year',
};

const validationSchema = yup.object({
	fromDate: yup
		.date()
		.required('This field is required')
		.test({
			name: 'fromLessThanTo',
			message: 'From date should be less than equal To date',
			test: function (fromDate: any) {
				if (fromDate && this.parent.toDate) {
					return moment(fromDate)
						.endOf('day')
						.isSameOrBefore(moment(this.parent.toDate).endOf('day'));
				}
				return true;
			},
		}),
	toDate: yup
		.date()
		.required('This field is required')
		.test({
			name: 'toDateShouldBeLessThanEqualToday',
			message: 'To date should be less than equal to today',
			test: function (toDate: any) {
				if (this.parent.toDate) {
					return toDate <= new Date();
				}
				return true;
			},
		}),
});

const getInitialValues = (): IEndUsersCountChartForm => ({
	fromDate: moment().add(-7, 'day').toDate(),
	toDate: moment().toDate(),
	type: EndUsersCountChartType.NEW,
});

const getChartProps = (
	{ datesRangeUnit, stats }: IEndUsersCountStatsByDate,
	fromDate: Date,
	toDate: Date,
	barBackground: string,
): IChartProps => {
	let xaxis: ApexXAxis = {
		type: 'category',
		categories: [],
		title: {
			text: axisTitle[datesRangeUnit],
			style: {
				fontSize: '18px',
			},
		},
	};
	let yaxis: number[] = [];
	switch (datesRangeUnit) {
		case 'd':
			const allDays = generateDatesRangeArray(
				fromDate,
				toDate,
				'day',
				'YYYY-MM-DD',
			);

			yaxis = allDays.map(day => {
				const foundStat = stats.find(stat => stat.dailyDate === day);
				return foundStat?.totalCount || 0;
			});
			xaxis.categories = allDays.map(day => {
				return moment(day).format('ddd MMM Do YY');
			});

			break;
		case 'w':
			const allWeeks = generateDatesRangeArray(
				fromDate,
				toDate,
				'week',
				'YYYY-W',
			);
			yaxis = allWeeks.map(week => {
				const foundStat = stats.find(stat => stat.weeklyDate === week);
				return foundStat?.totalCount || 0;
			});
			xaxis.categories = allWeeks.map(week => {
				const date = moment(week, 'YYYY-W');
				return 'Week ' + date.format('W | MMM YY');
			});
			break;
		case 'm':
			const allMonths = generateDatesRangeArray(
				fromDate,
				toDate,
				'month',
				'YYYY-MM',
			);
			yaxis = allMonths.map(month => {
				const foundStat = stats.find(stat => stat.monthlyDate === month);
				return foundStat?.totalCount || 0;
			});
			xaxis.categories = allMonths.map(month => {
				return moment(month).format('MMM YY');
			});

			break;

		case 'y':
			const allYears = generateDatesRangeArray(
				fromDate,
				toDate,
				'year',
				'YYYY',
			);
			yaxis = allYears.map(year => {
				const foundStat = stats.find(stat => stat.yearlyDate === year);

				return foundStat?.totalCount || 0;
			});
			xaxis.categories = allYears;
			break;
	}
	return {
		height: 500,
		type: 'bar',
		options: {
			colors: [barBackground],
			title: {
				text: '',
				align: 'center',
				style: {
					fontSize: '24px',
				},
			},
			chart: {
				id: 'bar-chart',
				stacked: true,
				toolbar: {
					show: true,
				},
				zoom: {
					enabled: true,
				},
				animations: {
					enabled: true,
					easing: 'easeinout',
					speed: 800,
					animateGradually: {
						enabled: true,
						delay: 150,
					},
					dynamicAnimation: {
						enabled: true,
						speed: 350,
					},
				},
			},
			responsive: [
				{
					breakpoint: 480,
					options: {
						legend: {
							position: 'bottom',
							offsetX: -10,
							offsetY: 0,
						},
					},
				},
			],
			plotOptions: {
				bar: {
					borderRadius: 4,
					horizontal: false,
				},
			},
			xaxis: xaxis,
			yaxis: {
				title: {
					text: 'Number of users',
					style: {
						fontSize: '18px',
					},
				},
				tickAmount: 1,
				labels: {
					formatter: value => {
						return value.toString();
					},
				},
			},
			legend: {
				show: true,
				fontSize: '14px',
				fontFamily: `'Roboto', sans-serif`,
				position: 'bottom',
				offsetX: 20,
				labels: {
					useSeriesColors: false,
				},
				markers: {
					width: 16,
					height: 16,
					radius: 5,
				},
				itemMargin: {
					horizontal: 15,
					vertical: 8,
				},
			},
			fill: {
				type: 'solid',
			},

			grid: {
				show: true,
			},
			dataLabels: {
				enabled: true,
				formatter: function (val) {
					return val.toString();
				},
				offsetY: 0,
				style: {
					fontSize: '12px',
				},
			},
		},

		series: [
			{
				name: 'Total users',
				data: yaxis,
			},
		],
	};
};
